import { createApi } from "@reduxjs/toolkit/query/react";

import type { CreateDerivedDatasetDTO, DatasetDTO } from "../../../types/Dataset";
import { createServiceBaseQuery } from "../../../../../store/client/serviceBaseQuery";
import { sortDTOByKeys } from "../../../utils/datasetHelpers";
import { LogicalType, PhysicalType } from "../../../types/Field";
import { DatasetHistoryDTO } from "../../../types/DatasetHistory";
import { QueryRow } from "../../../types/QueryRow";

export interface UploadDTO {
  datasetName: string;
  records: Record<string, string | number>[];
  physicalTypes?: Record<string, PhysicalType>;
  logicalTypes?: Record<string, LogicalType>;
  mergeKeys?: string[];
  description?: string;
}

export type UpdateUploadDTO = Pick<UploadDTO, "records">;

export type UploadResponseDTO = DatasetDTO & {
  extra: {
    mergeKeys: string[];
  };
};

export type EditDatasetRecordsDTO = {
  delete: number[];
  upsert: QueryRow[];
};

export const datasetService = createApi({
  reducerPath: "datasetService",
  baseQuery: createServiceBaseQuery("orpheusUri"),
  tagTypes: ["Datasets"],
  refetchOnReconnect: true,
  endpoints: (builder) => ({
    getDatasets: builder.query<DatasetDTO[], void>({
      query: () => {
        return "/api/application-datasets";
      },
      providesTags: [{ type: "Datasets", id: "LIST" }],
      transformResponse: (responseData: DatasetDTO[]) => {
        const sortedData = [...responseData].sort(sortDTOByKeys<DatasetDTO>(["name"]));
        return sortedData;
      },
    }),
    getDatasetById: builder.query<DatasetDTO, { datasetId: string }>({
      query: (arg) => {
        const { datasetId } = arg;
        return `/api/datasets/${datasetId}`;
      },
      providesTags: [{ type: "Datasets", id: "DETAIL" }],
    }),
    getDatasetHistory: builder.query<DatasetHistoryDTO[], { datasetId: string }>({
      query: (arg) => {
        const { datasetId } = arg;
        return `/api/datasets/${datasetId}/history`;
      },
      providesTags: [{ type: "Datasets", id: "HISTORY_LIST" }],
    }),
    updateDatasetById: builder.mutation<DatasetDTO, { dataset: DatasetDTO }>({
      query: (arg) => {
        const { dataset } = arg;
        const datasetId = dataset.id;
        return {
          url: `/api/datasets/${datasetId}`,
          method: "PUT",
          body: dataset,
        };
      },
      invalidatesTags: [
        { type: "Datasets", id: "LIST" },
        { type: "Datasets", id: "DETAIL" },
        { type: "Datasets", id: "HISTORY_LIST" },
      ],
    }),
    createDerivedDataset: builder.mutation<
      CreateDerivedDatasetDTO,
      { dataset: CreateDerivedDatasetDTO }
    >({
      query: (arg) => ({
        url: `/api/datasets`,
        method: "POST",
        body: arg.dataset,
      }),
      invalidatesTags: [{ type: "Datasets", id: "LIST" }],
    }),
    uploadDataset: builder.mutation<DatasetDTO, { data: UploadDTO }>({
      query: (arg) => ({
        url: `/api/datasets/upload`,
        method: "POST",
        body: arg.data,
      }),
      invalidatesTags: [{ type: "Datasets", id: "LIST" }],
    }),
    deleteDataset: builder.mutation<void, { datasetId: string }>({
      query: (arg) => {
        const { datasetId } = arg;
        return {
          url: `/api/datasets/${datasetId}`,
          method: "DELETE",
        };
      },
      invalidatesTags: [{ type: "Datasets", id: "LIST" }],
    }),
    restoreDataset: builder.mutation<void, { datasetId: string; historyId: string }>({
      query: (arg) => {
        const { datasetId, historyId } = arg;
        return {
          url: `/api/datasets/${datasetId}/restore`,
          method: "POST",
          body: {
            historyId,
          },
        };
      },
      invalidatesTags: [{ type: "Datasets", id: "HISTORY_LIST" }],
    }),
    restoreDatasetMetadata: builder.mutation<
      void,
      { datasetId: string; targetVersion: number }
    >({
      query: ({ datasetId, targetVersion }) => ({
        url: `/api/datasets/${datasetId}/metadata-restore`,
        method: "POST",
        body: { targetVersion },
      }),
      invalidatesTags: [
        { type: "Datasets", id: "HISTORY_LIST" },
        { type: "Datasets", id: "LIST" },
        { type: "Datasets", id: "DETAIL" },
      ],
    }),
    updateUploadDataset: builder.mutation<
      void,
      { data: UpdateUploadDTO; datasetId: string }
    >({
      query: (arg) => ({
        url: `/api/datasets/${arg.datasetId}/upload`,
        method: "POST",
        body: arg.data,
      }),
      invalidatesTags: [{ type: "Datasets", id: "LIST" }],
    }),
    replaceUploadDataset: builder.mutation<
      void,
      { data: UpdateUploadDTO; datasetId: string }
    >({
      query: (arg) => ({
        url: `/api/datasets/${arg.datasetId}/replace`,
        method: "POST",
        body: arg.data,
      }),
      invalidatesTags: [{ type: "Datasets", id: "LIST" }],
    }),
    editDatasetRecords: builder.mutation<
      void,
      { data: EditDatasetRecordsDTO; datasetId: string }
    >({
      query: (arg) => ({
        url: `/api/datasets/${arg.datasetId}/edit_records`,
        method: "POST",
        body: arg.data,
      }),
      invalidatesTags: [{ type: "Datasets", id: "LIST" }],
    }),
  }),
});

export const {
  useGetDatasetsQuery,
  useGetDatasetByIdQuery,
  useUpdateDatasetByIdMutation,
  useCreateDerivedDatasetMutation,
  useUploadDatasetMutation,
  useDeleteDatasetMutation,
  useGetDatasetHistoryQuery,
  useRestoreDatasetMutation,
  useRestoreDatasetMetadataMutation,
  useUpdateUploadDatasetMutation,
  useReplaceUploadDatasetMutation,
  useEditDatasetRecordsMutation,
} = datasetService;
